export type SidebarMode = 'expanded' | 'collapsed' | 'auto' | 'manual';

const STORAGE_KEY = 'aktify_sidebar_mode';
/** Manual's own open/closed state. Separate so switching modes and back remembers where it was. */
const MANUAL_KEY = 'aktify_sidebar_manual_collapsed';
/** Pre-modes key, a plain boolean. Read once so an existing preference survives the upgrade. */
const LEGACY_KEY = 'aktify_sidebar_collapsed';

const isMode = (v: string | null): v is SidebarMode =>
  v === 'expanded' || v === 'collapsed' || v === 'auto' || v === 'manual';

/** Defaults to `auto` — what an untouched sidebar already did. A legacy boolean maps onto it. */
export const readSidebarMode = (): SidebarMode => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (isMode(raw)) return raw;

    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === 'true') return 'collapsed';
    if (legacy === 'false') return 'expanded';

    return 'auto';
  } catch {
    return 'auto';
  }
};

export const writeSidebarMode = (mode: SidebarMode): void => {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch (e) {
    console.error('Error accessing localStorage:', e);
  }
};

/** `/campaigns/:id` also matches the list pages, whose "id" is one of these words. */
const CAMPAIGN_LIST_SEGMENTS = new Set(['all', 'deprovision', 'create']);

/** Covers both trees: `/campaigns/:id/...` and `/companies/:id/campaigns/:campaignId/...`. */
export const isInsideCampaign = (pathname: string): boolean => {
  const match = /\/campaigns\/([^/]+)/.exec(pathname);
  return !!match && !CAMPAIGN_LIST_SEGMENTS.has(match[1]);
};

/**
 * Whether the sidebar holds itself open — which is also what reserves layout width. A peeking
 * sidebar is deliberately *not* pinned: it floats over the page so nothing reflows under it.
 */
export const isSidebarPinned = (
  mode: SidebarMode,
  showingCampaign: boolean,
  manualCollapsed: boolean,
): boolean =>
  mode === 'expanded' ||
  (mode === 'auto' && !showingCampaign) ||
  (mode === 'manual' && !manualCollapsed);

/** Only auto opens on hover: `collapsed` is an explicit choice and `manual` shows a handle. */
export const canSidebarPeek = (mode: SidebarMode, showingCampaign: boolean): boolean =>
  // manualCollapsed is irrelevant on the auto branch, but passing it keeps the signature honest.
  mode === 'auto' && !isSidebarPinned(mode, showingCampaign, false);

export const readManualCollapsed = (): boolean => {
  try {
    return localStorage.getItem(MANUAL_KEY) === 'true';
  } catch {
    return false;
  }
};

export const writeManualCollapsed = (collapsed: boolean): void => {
  try {
    localStorage.setItem(MANUAL_KEY, String(collapsed));
  } catch (e) {
    console.error('Error accessing localStorage:', e);
  }
};
